import { useApp } from "@/context/AppContext";
import { useSEO } from "@/hooks/useSEO";
import { useSearchParams, Navigate } from "react-router-dom";
import { KEYWORDS } from "@/lib/seoContent";
import { webPageLd, breadcrumbLd, organizationLd } from "@/lib/structuredData";

const CONTENT = {
  en: {
    impressum: {
      metaTitle: "Imprint — NetoVibe",
      metaDesc: "Legal disclosure for NetoVibe web design studio in accordance with § 5 TMG.",
      eyebrow: "Legal",
      title: "Imprint",
      lead: "Information required by § 5 TMG (Telemediengesetz).",
      sections: [
        {
          h: "Provider",
          rows: [
            ["Company", "NetoVibe — Web Design Studio"],
            ["Owner", "Founder & Lead Developer"],
            ["Address", "Berlin, Germany · Remote worldwide"],
            ["Email", "netovibe@outlook.com"],
            ["Phone", "+49 155 6944 5772"],
          ],
        },
        {
          h: "Responsible for content (§ 55 Abs. 2 RStV)",
          rows: [["Editorial responsibility", "NetoVibe — Berlin, Germany"]],
        },
        {
          h: "Liability for content",
          body: [
            "As a service provider we are responsible for our own content on these pages under § 7 (1) TMG and the general laws. According to §§ 8 to 10 TMG we are not obligated to monitor transmitted or stored external information, or to investigate circumstances that indicate illegal activity.",
            "Obligations to remove or block the use of information under the general laws remain unaffected. Liability is only possible from the moment we become aware of a concrete infringement. As soon as we become aware of any such legal infringements we will remove this content immediately.",
          ],
        },
        {
          h: "Liability for links",
          body: [
            "Our site contains links to external third-party websites over whose content we have no influence. Therefore we cannot assume any liability for this external content. The respective provider or operator of the pages is always responsible for the content of the linked pages.",
          ],
        },
        {
          h: "Copyright",
          body: [
            "The content and works created by the site operators on these pages are subject to German copyright law. Duplication, processing, distribution, or any form of commercialisation of such material beyond the scope of the copyright law shall require the prior written consent of its respective author or creator.",
          ],
        },
        {
          h: "Dispute resolution",
          body: [
            "The European Commission provides an online dispute resolution platform (OS) at: https://ec.europa.eu/consumers/odr. We are not willing or obligated to participate in dispute resolution proceedings before a consumer arbitration board.",
          ],
        },
      ],
    },
    datenschutz: {
      metaTitle: "Privacy Policy — NetoVibe",
      metaDesc: "Privacy policy for NetoVibe: how we handle personal data submitted via our contact form, in compliance with the GDPR.",
      eyebrow: "Legal",
      title: "Privacy Policy",
      lead:
        "This policy explains how NetoVibe collects, uses, and protects the personal data you share with us — in plain language, and in line with the EU General Data Protection Regulation (GDPR / DSGVO).",
      sections: [
        {
          h: "1. Controller",
          body: [
            "The controller responsible for data processing on this website is NetoVibe (see Imprint for full contact details). Questions about privacy can be sent to netovibe@outlook.com.",
          ],
        },
        {
          h: "2. What data we collect",
          body: [
            "Contact form: name, email address, optional company name, business type, project budget range, message content, and the language of the interface. Server logs: standard technical data such as anonymised IP address, browser type, and timestamp, retained only for security purposes.",
          ],
        },
        {
          h: "3. Purpose & legal basis",
          body: [
            "We process contact-form data to respond to your enquiry and to prepare a quote (Art. 6 (1)(b) GDPR — pre-contractual measures). Server logs are processed on the basis of our legitimate interest in operating a secure website (Art. 6 (1)(f) GDPR).",
          ],
        },
        {
          h: "4. Storage duration",
          body: [
            "Contact submissions are retained for as long as necessary to handle your enquiry and any resulting engagement, and deleted afterwards unless statutory retention periods apply. Server logs are rotated regularly.",
          ],
        },
        {
          h: "5. Recipients",
          body: [
            "Data is processed on infrastructure operated by our hosting provider inside the EU. We do not sell or share your personal data with third parties for marketing purposes.",
          ],
        },
        {
          h: "6. Your rights",
          body: [
            "You have the right to access, rectify, delete, restrict, port, and object to the processing of your personal data, as well as the right to lodge a complaint with a supervisory authority. To exercise any of these rights, contact us at netovibe@outlook.com.",
          ],
        },
        {
          h: "7. Cookies",
          body: [
            "This site uses only strictly necessary storage (localStorage) to remember your language and currency preference. No advertising or cross-site tracking cookies are set by NetoVibe.",
          ],
        },
        {
          h: "8. Updates",
          body: [
            "We may update this policy to reflect legal or operational changes. The current version is always published on this page.",
          ],
        },
      ],
    },
  },
  de: {
    impressum: {
      metaTitle: "Impressum — NetoVibe",
      metaDesc: "Anbieterkennzeichnung des Webdesign-Studios NetoVibe gemäß § 5 TMG.",
      eyebrow: "Rechtliches",
      title: "Impressum",
      lead: "Angaben gemäß § 5 TMG (Telemediengesetz).",
      sections: [
        {
          h: "Anbieter",
          rows: [
            ["Unternehmen", "NetoVibe — Webdesign Studio"],
            ["Inhaber", "Gründer & Lead-Entwickler"],
            ["Adresse", "Berlin, Deutschland · Remote weltweit"],
            ["E-Mail", "netovibe@outlook.com"],
            ["Telefon", "+49 155 6944 5772"],
          ],
        },
        {
          h: "Verantwortlich für den Inhalt (§ 55 Abs. 2 RStV)",
          rows: [["Redaktionelle Verantwortung", "NetoVibe — Berlin, Deutschland"]],
        },
        {
          h: "Haftung für Inhalte",
          body: [
            "Als Diensteanbieter sind wir gemäß § 7 Abs. 1 TMG für eigene Inhalte auf diesen Seiten nach den allgemeinen Gesetzen verantwortlich. Nach §§ 8 bis 10 TMG sind wir jedoch nicht verpflichtet, übermittelte oder gespeicherte fremde Informationen zu überwachen oder nach Umständen zu forschen, die auf eine rechtswidrige Tätigkeit hinweisen.",
            "Verpflichtungen zur Entfernung oder Sperrung der Nutzung von Informationen nach den allgemeinen Gesetzen bleiben hiervon unberührt. Eine diesbezügliche Haftung ist jedoch erst ab dem Zeitpunkt der Kenntnis einer konkreten Rechtsverletzung möglich. Bei Bekanntwerden entsprechender Rechtsverletzungen werden wir diese Inhalte umgehend entfernen.",
          ],
        },
        {
          h: "Haftung für Links",
          body: [
            "Unser Angebot enthält Links zu externen Websites Dritter, auf deren Inhalte wir keinen Einfluss haben. Deshalb können wir für diese fremden Inhalte auch keine Gewähr übernehmen. Für die Inhalte der verlinkten Seiten ist stets der jeweilige Anbieter oder Betreiber der Seiten verantwortlich.",
          ],
        },
        {
          h: "Urheberrecht",
          body: [
            "Die durch die Seitenbetreiber erstellten Inhalte und Werke auf diesen Seiten unterliegen dem deutschen Urheberrecht. Die Vervielfältigung, Bearbeitung, Verbreitung und jede Art der Verwertung außerhalb der Grenzen des Urheberrechtes bedürfen der schriftlichen Zustimmung des jeweiligen Autors bzw. Erstellers.",
          ],
        },
        {
          h: "Streitschlichtung",
          body: [
            "Die Europäische Kommission stellt eine Plattform zur Online-Streitbeilegung (OS) bereit: https://ec.europa.eu/consumers/odr. Wir sind nicht bereit oder verpflichtet, an Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle teilzunehmen.",
          ],
        },
      ],
    },
    datenschutz: {
      metaTitle: "Datenschutzerklärung — NetoVibe",
      metaDesc: "Datenschutzerklärung von NetoVibe: Umgang mit personenbezogenen Daten aus dem Kontaktformular nach DSGVO.",
      eyebrow: "Rechtliches",
      title: "Datenschutzerklärung",
      lead:
        "Diese Erklärung beschreibt, wie NetoVibe personenbezogene Daten erhebt, verwendet und schützt — verständlich formuliert und im Einklang mit der EU-Datenschutz-Grundverordnung (DSGVO).",
      sections: [
        {
          h: "1. Verantwortlicher",
          body: [
            "Verantwortlich für die Datenverarbeitung auf dieser Website ist NetoVibe (siehe Impressum für die vollständigen Kontaktdaten). Fragen zum Datenschutz senden Sie bitte an netovibe@outlook.com.",
          ],
        },
        {
          h: "2. Welche Daten wir erheben",
          body: [
            "Kontaktformular: Name, E-Mail-Adresse, optional Firmenname, Art des Unternehmens, Projektbudget, Nachrichtentext sowie die Sprache der Oberfläche. Server-Logs: technische Standarddaten wie anonymisierte IP-Adresse, Browser-Typ und Zeitstempel — ausschließlich zu Sicherheitszwecken.",
          ],
        },
        {
          h: "3. Zweck & Rechtsgrundlage",
          body: [
            "Wir verarbeiten die Daten aus dem Kontaktformular, um Ihre Anfrage zu beantworten und ein Angebot vorzubereiten (Art. 6 Abs. 1 lit. b DSGVO — vorvertragliche Maßnahmen). Server-Logs verarbeiten wir auf Grundlage unseres berechtigten Interesses am sicheren Betrieb der Website (Art. 6 Abs. 1 lit. f DSGVO).",
          ],
        },
        {
          h: "4. Speicherdauer",
          body: [
            "Kontaktanfragen werden so lange gespeichert, wie es zur Bearbeitung Ihrer Anfrage und einer daraus resultierenden Zusammenarbeit erforderlich ist, und anschließend gelöscht — sofern keine gesetzlichen Aufbewahrungspflichten bestehen. Server-Logs werden regelmäßig rotiert.",
          ],
        },
        {
          h: "5. Empfänger",
          body: [
            "Die Verarbeitung erfolgt auf einer Infrastruktur unseres Hosting-Anbieters innerhalb der EU. Wir verkaufen personenbezogene Daten nicht und geben sie nicht zu Marketingzwecken an Dritte weiter.",
          ],
        },
        {
          h: "6. Ihre Rechte",
          body: [
            "Sie haben das Recht auf Auskunft, Berichtigung, Löschung, Einschränkung, Datenübertragbarkeit und Widerspruch gegen die Verarbeitung Ihrer personenbezogenen Daten sowie das Recht, sich bei einer Aufsichtsbehörde zu beschweren. Zur Ausübung genügt eine kurze Nachricht an netovibe@outlook.com.",
          ],
        },
        {
          h: "7. Cookies",
          body: [
            "Diese Website nutzt ausschließlich technisch notwendigen Speicher (localStorage), um Ihre Sprach- und Währungseinstellung zu merken. NetoVibe setzt keine Werbe- oder seitenübergreifenden Tracking-Cookies.",
          ],
        },
        {
          h: "8. Aktualisierungen",
          body: [
            "Wir können diese Erklärung bei rechtlichen oder betrieblichen Änderungen anpassen. Die jeweils aktuelle Fassung wird immer auf dieser Seite veröffentlicht.",
          ],
        },
      ],
    },
  },
};

const Legal = ({ page }) => {
  const { t, lang } = useApp();
  const [search] = useSearchParams();
  const urlLang = search.get("lang");
  const activeLang = ["en", "de"].includes(urlLang) ? urlLang : lang;
  const content = CONTENT[activeLang]?.[page];

  useSEO({
    title: content?.metaTitle,
    description: content?.metaDesc,
    path: `/${page}`,
    lang: activeLang,
    keywords: KEYWORDS[activeLang]?.legal,
    robots: "noindex, follow",
    jsonLd: content
      ? [
          organizationLd(),
          webPageLd({
            path: `/${page}`,
            name: content.metaTitle,
            description: content.metaDesc,
            lang: activeLang,
          }),
          breadcrumbLd([
            { name: activeLang === "de" ? "Start" : "Home", path: "/" },
            { name: content.title, path: `/${page}` },
          ]),
        ]
      : [],
  });

  if (!content) return <Navigate to="/" replace />;

  const pad2 = (n) => String(n).padStart(2, "0");
  const slugify = (s) =>
    (s || "")
      .toLowerCase()
      .normalize("NFKD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");

  const legalRefBadge = page === "impressum" ? "TMG · § 5" : "GDPR · DSGVO";

  return (
    <main data-testid={`${page}-page`} className="pt-16 lg:pt-20 bg-white">
      {/* HERO */}
      <section className="relative border-b border-[#0A0A0A] bg-[#FAFAFA] overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none opacity-[0.05]"
          style={{
            backgroundImage:
              "linear-gradient(to right, #0A0A0A 1px, transparent 1px), linear-gradient(to bottom, #0A0A0A 1px, transparent 1px)",
            backgroundSize: "56px 56px",
          }}
          aria-hidden
        />
        <div className="relative mx-auto max-w-[1400px] px-6 lg:px-10 pt-20 lg:pt-28 pb-14 lg:pb-20 grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-8">
            <div className="flex items-center gap-3 text-xs tracking-[0.2em] uppercase font-bold text-[#0A0A0A]">
              <span className="inline-block h-[1px] w-8 bg-[#3A00FF]" />
              {content.eyebrow}
              <span className="ml-3 border border-[#0A0A0A] px-2 py-1 text-[10px] tracking-[0.24em] uppercase font-bold bg-white">
                {legalRefBadge}
              </span>
            </div>
            <h1
              data-testid={`${page}-title`}
              className="mt-6 text-5xl sm:text-6xl lg:text-7xl xl:text-[6.5rem] leading-[0.9] tracking-tighter font-black text-[#0A0A0A] font-[Cabinet_Grotesk]"
            >
              {content.title}
            </h1>
            <p className="mt-8 max-w-2xl text-base sm:text-lg text-[#525252] leading-relaxed">
              {content.lead}
            </p>
          </div>
          <aside className="lg:col-span-4 lg:justify-self-end w-full">
            <div className="border border-[#0A0A0A] bg-white shadow-[6px_6px_0px_#3A00FF] p-6 lg:p-7">
              <div className="text-[10px] tracking-[0.24em] uppercase font-bold text-[#3A00FF]">
                {activeLang === "de" ? "Dokument" : "Document"}
              </div>
              <div className="mt-3 font-mono text-xs text-[#0A0A0A] space-y-1">
                <div>
                  <span className="text-[#525252]">ref/</span>
                  {page === "impressum" ? "netovibe-imprint-v1" : "netovibe-privacy-v1"}
                </div>
                <div>
                  <span className="text-[#525252]">lang/</span>
                  {activeLang === "de" ? "de-DE" : "en-US"}
                </div>
                <div>
                  <span className="text-[#525252]">rev/</span>2026-07-02
                </div>
                <div>
                  <span className="text-[#525252]">sections/</span>
                  {content.sections.length}
                </div>
              </div>
            </div>
          </aside>
        </div>
      </section>

      {/* CONTENT + STICKY TOC */}
      <section className="border-b border-[#0A0A0A]">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-14 lg:py-20 grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Sticky Table of Contents (desktop) */}
          <aside className="hidden lg:block lg:col-span-3">
            <div className="sticky top-28">
              <div className="text-[10px] tracking-[0.24em] uppercase font-bold text-[#0A0A0A]">
                {activeLang === "de" ? "Inhalt" : "Contents"}
              </div>
              <ol className="mt-4 space-y-3 border-l border-[#0A0A0A]/20 pl-4">
                {content.sections.map((s, i) => (
                  <li key={i}>
                    <a
                      href={`#s-${i}`}
                      data-testid={`${page}-toc-${i}`}
                      className="group flex items-start gap-2 text-sm text-[#525252] hover:text-[#0A0A0A] transition-colors"
                    >
                      <span className="font-mono text-[11px] text-[#3A00FF] mt-0.5">
                        §{pad2(i + 1)}
                      </span>
                      <span className="leading-snug group-hover:underline underline-offset-4 decoration-[#3A00FF] decoration-2">
                        {s.h}
                      </span>
                    </a>
                  </li>
                ))}
              </ol>
              <div className="mt-8 border border-[#0A0A0A] p-4 text-xs text-[#525252] leading-relaxed">
                <div className="text-[10px] tracking-[0.24em] uppercase font-bold text-[#0A0A0A] mb-2">
                  {activeLang === "de" ? "Kontakt" : "Contact"}
                </div>
                <a
                  href="mailto:netovibe@outlook.com"
                  className="block hover:text-[#3A00FF]"
                >
                  netovibe@outlook.com
                </a>
                <a
                  href="tel:+4915569445772"
                  className="block hover:text-[#3A00FF]"
                >
                  +49 155 6944 5772
                </a>
              </div>
            </div>
          </aside>

          {/* Sections */}
          <div className="lg:col-span-9">
            {/* Mobile TOC */}
            <details className="lg:hidden group mb-10 border border-[#0A0A0A]">
              <summary className="flex items-center justify-between cursor-pointer list-none px-4 py-3 text-xs tracking-[0.24em] uppercase font-bold">
                {activeLang === "de" ? "Inhaltsverzeichnis" : "Table of contents"}
                <span className="font-mono text-[11px] text-[#3A00FF] group-open:rotate-90 transition-transform">
                  ↦
                </span>
              </summary>
              <ol className="border-t border-[#0A0A0A] px-4 py-3 space-y-2">
                {content.sections.map((s, i) => (
                  <li key={i}>
                    <a
                      href={`#s-${i}`}
                      className="flex items-start gap-2 text-sm text-[#0A0A0A]"
                    >
                      <span className="font-mono text-[11px] text-[#3A00FF]">
                        §{pad2(i + 1)}
                      </span>
                      <span>{s.h}</span>
                    </a>
                  </li>
                ))}
              </ol>
            </details>

            <div className="space-y-14 lg:space-y-20">
              {content.sections.map((s, i) => (
                <article
                  key={i}
                  id={`s-${i}`}
                  data-testid={`${page}-section-${i}`}
                  className="relative scroll-mt-24"
                >
                  <div className="flex items-start gap-6">
                    <div className="hidden sm:block shrink-0">
                      <div className="font-[Cabinet_Grotesk] font-black text-5xl lg:text-6xl tracking-tighter leading-none text-[#3A00FF]/15">
                        {pad2(i + 1)}
                      </div>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-3">
                        <span className="font-mono text-[10px] tracking-[0.24em] uppercase text-[#3A00FF]">
                          §{pad2(i + 1)}
                        </span>
                        <span className="h-px flex-1 bg-[#0A0A0A]/15" />
                      </div>
                      <h2 className="mt-3 text-2xl lg:text-3xl leading-tight tracking-tight font-black text-[#0A0A0A] font-[Cabinet_Grotesk]">
                        {s.h}
                      </h2>

                      {s.body && (
                        <div className="mt-6 space-y-4 text-sm lg:text-base leading-relaxed text-[#525252] max-w-prose">
                          {s.body.map((p, j) => (
                            <p key={j}>{p}</p>
                          ))}
                        </div>
                      )}

                      {s.rows && (
                        <dl className="mt-6 border border-[#0A0A0A] divide-y divide-[#0A0A0A]/15 bg-white">
                          {s.rows.map(([k, v], j) => (
                            <div
                              key={j}
                              className="grid grid-cols-1 sm:grid-cols-12 gap-2 sm:gap-4 px-5 py-4"
                            >
                              <dt className="sm:col-span-4 text-[10px] tracking-[0.24em] uppercase font-bold text-[#0A0A0A]">
                                {k}
                              </dt>
                              <dd className="sm:col-span-8 text-sm text-[#0A0A0A] break-words">
                                {typeof v === "string" &&
                                (v.includes("@") || /^\+?[\d\s()+-]+$/.test(v)) ? (
                                  <a
                                    href={
                                      v.includes("@")
                                        ? `mailto:${v}`
                                        : `tel:${v.replace(/[^\d+]/g, "")}`
                                    }
                                    className="hover:text-[#3A00FF] transition-colors font-mono text-[13px]"
                                  >
                                    {v}
                                  </a>
                                ) : (
                                  <span>{v}</span>
                                )}
                              </dd>
                            </div>
                          ))}
                        </dl>
                      )}
                    </div>
                  </div>
                </article>
              ))}
            </div>

            {/* Footer note + back-to-top */}
            <div className="mt-16 lg:mt-24 border-t border-[#0A0A0A]/15 pt-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <p className="text-xs text-[#525252] max-w-xl">
                {activeLang === "de"
                  ? "Diese Seite dient der rechtlichen Transparenz und ersetzt keine individuelle Rechtsberatung."
                  : "This page is provided for legal transparency and does not constitute individual legal advice."}
              </p>
              <a
                href="#top"
                onClick={(e) => {
                  e.preventDefault();
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
                className="inline-flex items-center gap-2 text-[10px] tracking-[0.24em] uppercase font-bold text-[#0A0A0A] hover:text-[#3A00FF]"
              >
                {activeLang === "de" ? "Nach oben" : "Back to top"}
                <span className="font-mono">↑</span>
              </a>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Legal;
